import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import { s3baseUrl } from "../../config/config";
import { ReactVideoPlayer } from "src/components";
import { programmeDetail } from "src/DAL/Programmes/Programmes";
import ConfirmationWithDate from "src/components/_dashboard/programmes/ConfirmationWithDate";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrPage from "src/design-system/components/GrPage";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrCard from "src/design-system/components/GrCard";
import GrCardHeader from "src/design-system/components/GrCardHeader";
import GrButton from "src/design-system/components/GrButton";
import GrKeyValueList from "src/design-system/components/GrKeyValueList";
import GrIcon from "src/design-system/components/GrIcon";
import GrEmptyState from "src/design-system/components/GrEmptyState";
import GrRichText from "src/design-system/components/GrRichText";
import GrCoverImage from "src/design-system/components/GrCoverImage";
import GrLoader from "src/design-system/components/GrLoader";
import {
  canOpenCalendar,
  endDate,
  formatExpireOn,
  formatStartedOn,
  htmlText,
  isLimited,
  lengthLabel,
} from "./programmeHelpers";

/**
 * A programme's detail (/programmes/:slug), opened from the Programmes list (the list entry is
 * the router state: start date, coins). Video or cover image, about text, key facts.
 */

const backLinkSx = {
  color: gr.color.ink2,
  fontWeight: gr.font.weight.regular,
  "&:hover": { color: gr.color.brandDark },
};
// One main card (video / image on top, "About this programme" under it) and the facts beside it;
// on phones the facts follow the main card.
const layoutSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    lg: "minmax(0, 1fr) minmax(280px, 360px)",
  },
  gap: gr.space[6],
  alignItems: "start",
};
const mainCardSx = {
  padding: 0,
  overflow: "hidden",
  "@media (max-width: 599.95px)": { padding: 0 },
};
const aboutSx = {
  p: gr.space[6],
  "@media (max-width: 599.95px)": { p: gr.space[5] },
};
export default function ProgrammesDetail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { state } = useLocation();
  const [starting, setStarting] = useState(false);

  const programmeId = state?._id?._id || state?.program_info?.program_id;

  const { data: programme, isLoading } = useQuery({
    queryKey: ["programmeDetail", programmeId],
    enabled: Boolean(programmeId),
    queryFn: async () => {
      const result = await programmeDetail(programmeId);
      if (result?.code !== 200) {
        throw new Error(result?.message);
      }
      return result.program;
    },
    onError: (error) => {
      enqueueSnackbar(error?.message, { variant: "error" });
    },
  });

  const backToList = () => navigate("/programmes");
  const item = state || {};
  const weeks = programme?.no_of_weeks;
  const lengthItem = {
    ...item,
    program_length_type:
      item.program_length_type || programme?.program_length_type,
  };
  const limited = isLimited(lengthItem);
  const open = canOpenCalendar(lengthItem);
  const ends = endDate(lengthItem, weeks);
  const slug = programme?.program_slug || item?._id?.program_slug;
  const title = htmlText(programme?.title || item.title) || "Programme";
  const image = programme?.program_images?.thumbnail_1;

  const mainAction = open ? (
    <GrButton
      onClick={() => navigate(`/programmes/calendar/${slug}`, { state: item })}
    >
      Open calendar
    </GrButton>
  ) : (
    <GrButton onClick={() => setStarting(true)}>Start programme</GrButton>
  );

  return (
    <GrPage>
      <Box sx={{ mb: gr.space[6] }}>
        <GrButton variant="link" onClick={backToList} sx={backLinkSx}>
          ← Programmes
        </GrButton>
      </Box>

      {!programmeId ? (
        <GrCard>
          <GrEmptyState
            icon={<GrIcon icon={icons.activity} />}
            title="Open this programme from your Programmes list."
            action={
              <GrButton variant="secondary" onClick={backToList}>
                Go to Programmes
              </GrButton>
            }
          />
        </GrCard>
      ) : isLoading ? (
        <GrLoader label="Loading the programme" />
      ) : !programme ? (
        <GrCard>
          <GrEmptyState
            icon={<GrIcon icon={icons.activity} />}
            title="This programme could not be loaded."
            action={
              <GrButton variant="secondary" onClick={backToList}>
                Back to Programmes
              </GrButton>
            }
          />
        </GrCard>
      ) : (
        <>
          <GrPageHeader
            title={title}
            subtitle={
              programme.short_description ||
              item?._id?.short_description ||
              null
            }
            actions={slug ? mainAction : null}
          />
          <Box sx={layoutSx}>
            <GrCard sx={mainCardSx}>
              {programme.video_url ? (
                <ReactVideoPlayer url={programme.video_url} />
              ) : image ? (
                <GrCoverImage src={s3baseUrl + image} alt={title} placeholderIcon={icons.activity} />
              ) : null}
              <Box sx={aboutSx}>
                <GrCardHeader as="h2" title="About this programme" />
                {htmlText(programme.detailed_description) ? (
                  <GrRichText html={programme.detailed_description} />
                ) : (
                  <GrEmptyState compact title="No description yet." />
                )}
              </Box>
            </GrCard>

            <GrCard>
              <GrCardHeader as="h2" title="At a glance" />
              <GrKeyValueList
                items={[
                  {
                    key: "length",
                    label: "Type",
                    value: lengthLabel(lengthItem),
                  },
                  limited
                    ? {
                        key: "start",
                        label: "Started On",
                        value: formatStartedOn(item.start_date),
                      }
                    : null,
                  limited
                    ? {
                        key: "end",
                        label: "Expire On",
                        value: formatExpireOn(ends),
                      }
                    : null,
                  // Ongoing: no start, the member has it from the day access was given.
                  !limited && item.purchase_date
                    ? {
                        key: "joined",
                        label: "Joined On",
                        value: formatStartedOn(item.purchase_date),
                      }
                    : null,
                  {
                    key: "coins",
                    label: "Coins earned",
                    value: item.total_points_earned || 0,
                  },
                ]}
              />
            </GrCard>
          </Box>
        </>
      )}

      <ConfirmationWithDate
        open={starting}
        // The dialog reads the programme from `_id`; a page opened with program_info has only its id.
        programId={item._id?._id ? item : { ...item, _id: programme || { _id: programmeId } }}
        setOpen={setStarting}
        reload={() => {
          queryClient.invalidateQueries({ queryKey: ["programmesList"] });
          navigate("/programmes");
        }}
      />
    </GrPage>
  );
}
